import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'next-i18next'

import { fetchPrefectureAreas } from 'features/prefecture/services/prefectureAreasApi'
import type { PrefecturesResponse } from 'features/prefecture/types/prefecture'
import { JP_COUNTRY_ID } from 'features/prefecture/constants'

type UsePrefecturesParams = {
  countryId?: number
  enabled?: boolean
}

export const usePrefectures = ({
  countryId = JP_COUNTRY_ID,
  enabled = true,
}: UsePrefecturesParams = {}) => {
  const { i18n } = useTranslation()
  const [prefectures, setPrefectures] = useState<PrefecturesResponse | null>(null)
  const [isLoading, setLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchPrefectures = useCallback(async () => {
    if (!countryId) return

    setLoading(true)
    setIsError(false)

    try {
      const { data } = await fetchPrefectureAreas({ locale: i18n.language, countryId })
      setPrefectures(data)
    } catch (error) {
      setIsError(true)
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { hook: 'usePrefectures' },
        extra: { locale: i18n.language, countryId },
      })
    } finally {
      setLoading(false)
    }
  }, [countryId, i18n.language])

  useEffect(() => {
    if (!enabled) return

    fetchPrefectures()
  }, [enabled, fetchPrefectures])

  return {
    prefectures,
    isLoading,
    isError,
    fetchPrefectures,
  }
}
